<script>
   /****************************************************************/
   /* ConfidenceEllipses                                           */
   /* --------------------                                         */
   /* shows confidence ellipses around each group of points        */
   /****************************************************************/

   import { Lines } from 'svelte-plots-basic/2d';
   import { Vector } from 'mdatools/arrays';
   import { qchisq } from 'mdatools/distributions';



   /*****************************************/
   /* Component parameters                  */
   /*****************************************/

   // inputs
   export let xValues;                       // vector or array with x-values
   export let yValues;                       // vector or array with y-values
   export let groups;                        // Array with group label for each value
   export let groupNames;                    // Array with unique label groups
   export let groupColors;
   export let confLevel = 0.95;              // Confidence level

   // outputs
   export let minYVal;                       // smallest y-value
   export let maxYVal;                       // largest y-value
   export let minXVal;                       // smallest x-value
   export let maxXVal;                       // largest x-value


   /*****************************************/
   /* Component code                        */
   /*****************************************/


   /**
    * Computes coordinates of ellipse.
    *
    * @param {Array} covMat - variance-covariance matrix for coordinates (xx, xy, yy).
    * @param {Array} center - coordinates of the center (x, y).
    * @param {Number} confLim - confidence limit.
    *
    * @returns {Arrat} array with two Vector instances — coordinates of the confidence ellipse points.
    */
   function getEllipse(covMat, center, confLevel = 0.95) {

      // compute eigenvalues
      const [a, b, d] = covMat;
      const D = Math.sqrt((a + d)**2 - 4 * (a * d - b * b));
      let l1 = 0.5 * (a + d - D);
      let l2 = 0.5 * (a + d + D);
      // swap eignevalues if needed so l1 ≥ l2
      if (l1 < l2) {
         let l = l1;
         l1 = l2;
         l2 = l;
      }

      // compute radii of the future ellipse
      const chisq = qchisq(confLevel, 2);
      const r1 = Math.sqrt(chisq * l1);
      const r2 = Math.sqrt(chisq * l2);

      let v1x, v1y, phi;
      if (Math.abs(b) < 10e-8) {
         v1x = 1;
         v1y = 0;
         phi = 0;
      } else {
         // compute largest eigenvactor and normalized it
         let v1x = -b / (a - l1);
         let v1y = 1;
         const n1 = Math.sqrt(v1x * v1x + v1y * v1y);
         v1x = v1x / n1;
         v1y = v1y / n1;
         phi = Math.atan2(v1y, v1x);
         if (phi < 0) phi = phi + 2 * Math.PI;
      }

      // get angle between the x-axis and the largest eigenvector

      // pre-compute cos and sin of the angle
      const cosphi = Math.cos(phi);
      const sinphi = Math.sin(phi);

      // define number of ellipse points and angle increments
      const nTheta = 100;
      const dTheta = 2 * Math.PI / (nTheta - 1);

      // prepare vectors for coordinates of the ellipse points
      const xVec = Vector.zeros(nTheta);
      const yVec = Vector.zeros(nTheta);

      // reset min/max values
      let xMin = Number.POSITIVE_INFINITY;
      let xMax = Number.NEGATIVE_INFINITY;
      let yMin = Number.POSITIVE_INFINITY;
      let yMax = Number.NEGATIVE_INFINITY;

      // compute and save coordinates of the ellipse points
      for (let i = 0; i < nTheta; i++) {
         const theta = i * dTheta;
         const x = r1 * Math.cos(theta);
         const y = r2 * Math.sin(theta);
         const xv = x * cosphi - y * sinphi + center[0];
         const yv = x * sinphi + y * cosphi + center[1];
         xVec.v[i] = xv;
         yVec.v[i] = yv;
         xMin = Math.min(xMin, xv);
         xMax = Math.max(xMax, xv);
         yMin = Math.min(yMin, yv);
         yMax = Math.max(yMax, yv);
      }

      return [xVec, yVec, xMin, xMax, yMin, yMax];
   }

   /**
    * Computes coordinates of ellipses for each group of points.
    *
    * @param {Vector} xValues - x-coordinates of points (on scatter plot).
    * @param {Vector} yValues - y-coordinates of the points.
    * @param {Array} groups - array with group label for each point.
    * @param {Array} groupNames - array with unique group labels.
    * @param {Array} groupColors - array with color for each group (same as used for the points).
    *
    * @returns {Array} array with JSON contining coordinates and colors.
    */
   function getEllipses(xValues, yValues, groups, groupNames, confLevel) {

      if (!xValues || !yValues || !groups || !groupNames) return null;
      const n = xValues.length;
      const nGroups = groupNames.length;

      // for each group compute sum of coordinates and number of points
      const numPoints = new Array(nGroups).fill(0);   // number of point in each group
      const centers = new Array(nGroups * 2).fill(0); // 2 x nGroups - each column is a group
      for (let i = 0; i < n; i++) {

         // index of group the value belongs to
         const ind = groupNames.indexOf(groups[i])

         // number of points in and sum of coordinates in this group
         numPoints[ind] += 1;
         centers[ind * 2] += xValues.v[i];
         centers[ind * 2 + 1] += yValues.v[i];
      }

      // divide sum of coordinates to the number of groups to get centers
      for (let i = 0; i < nGroups; i++) {
         centers[i * 2] = centers[i * 2] / numPoints[i];
         centers[i * 2 + 1] = centers[i * 2 + 1] / numPoints[i];
      }
      // compute elements of variance-coveriance matrix
      const covMat = new Array(nGroups * 3).fill(0); // 3 x nGroups - each column is a group
      for (let i = 0; i < n; i++) {

         // index of group the value belongs to
         const ind = groupNames.indexOf(groups[i])

         // elements of variance/covariance matrix (xx, xy, yy)

         const x = (xValues.v[i] - centers[ind * 2]);
         const y = (yValues.v[i] - centers[ind * 2 + 1]);
         covMat[ind * 3]     += x * x;
         covMat[ind * 3 + 1] += x * y;
         covMat[ind * 3 + 2] += y * y;
      }

      // reset min/max values
      let minXVal = Number.POSITIVE_INFINITY;
      let maxXVal = Number.NEGATIVE_INFINITY;
      let minYVal = Number.POSITIVE_INFINITY;
      let maxYVal = Number.NEGATIVE_INFINITY;

      // divide sum of coordinates to the number of groups to get centers and compute coordinates of ellipses
      ellipses = Array(nGroups);
      for (let i = 0; i < nGroups; i++) {
         const [xE, yE, xMin, xMax, yMin, yMax] = getEllipse(
            [covMat[i * 3] / (numPoints[i] - 1), covMat[i * 3 + 1] / (numPoints[i] - 1), covMat[i * 3 + 2] / (numPoints[i] - 1)],
            [centers[i * 2], centers[i * 2 + 1]],
            confLevel
         )
         ellipses[i] = {x: xE, y: yE, c: groupColors[i]};
         minXVal = Math.min(minXVal, xMin);
         maxXVal = Math.max(maxXVal, xMax);
         minYVal = Math.min(minYVal, yMin);
         maxYVal = Math.max(maxYVal, yMax);
      }

      return [ellipses, minXVal, maxXVal, minYVal, maxYVal];
   }

   $: [ellipses, minXVal, maxXVal, minYVal, maxYVal] = getEllipses(xValues, yValues, groups, groupNames, confLevel);
</script>

{#if ellipses }

   {#if ellipses}
   {#each ellipses as e, i}
   <Lines xValues={e.x} yValues={e.y} lineColor={groupColors[i]}/>
   {/each}
   {/if}

{/if}

